import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface CalendarEvent {
  id: string;
  workshop_id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  color: string;
}

interface Workshop {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

const PublicWorkshopCalendar: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [workshop, setWorkshop] = useState<Workshop | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  useEffect(() => {
    if (token) {
      loadCalendar();
    }
  }, [token]);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: linkData, error: linkError } = await supabase
        .from('workshop_calendar_links')
        .select('workshop_id, suppliers!inner(id, name, email, phone)')
        .eq('public_token', token)
        .eq('is_active', true)
        .maybeSingle();

      if (linkError || !linkData) {
        setError('Link no válido o expirado');
        setLoading(false);
        return;
      }

      const workshopData = (linkData as any).suppliers;
      setWorkshop({
        id: workshopData.id,
        name: workshopData.name,
        email: workshopData.email,
        phone: workshopData.phone
      });

      const { data: eventsData, error: eventsError } = await supabase
        .from('workshop_calendar_events')
        .select('*')
        .eq('workshop_id', linkData.workshop_id)
        .order('start_date', { ascending: true });

      if (eventsError) throw eventsError;

      setEvents(eventsData || []);
    } catch (error: any) {
      console.error('Error loading calendar:', error);
      setError('Error al cargar el calendario');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const monthEvents = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    return events.filter(event => {
      const eventStart = new Date(event.start_date);
      const eventEnd = new Date(event.end_date);
      return (eventStart <= lastDay && eventEnd >= firstDay);
    });
  }, [events, currentDate]);

  const getEventsForDate = (date: Date) => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    return events.filter(event => {
      const eventStart = new Date(event.start_date + 'T00:00:00');
      const eventEnd = new Date(event.end_date + 'T00:00:00');
      const eventStartOnly = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate());
      const eventEndOnly = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());

      return dateOnly >= eventStartOnly && dateOnly <= eventEndOnly;
    });
  };

  const renderCalendar = () => {
    const days = [];
    const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const startDate = new Date(yesterday);
    const DAYS_TO_SHOW = 30;

    const startDayOfWeek = startDate.getDay();

    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-gray-50"></div>);
    }

    for (let i = 0; i < DAYS_TO_SHOW; i++) {
      const currentDay = new Date(startDate);
      currentDay.setDate(startDate.getDate() + i);

      const dayEvents = getEventsForDate(currentDay);

      const isToday = today.getFullYear() === currentDay.getFullYear() &&
                      today.getMonth() === currentDay.getMonth() &&
                      today.getDate() === currentDay.getDate();

      const isYesterday = yesterday.getFullYear() === currentDay.getFullYear() &&
                          yesterday.getMonth() === currentDay.getMonth() &&
                          yesterday.getDate() === currentDay.getDate();

      const dayNumber = currentDay.getDate();
      const monthName = currentDay.toLocaleDateString('es-ES', { month: 'short' });
      const isNewMonth = i > 0 && currentDay.getDate() === 1;

      days.push(
        <div
          key={`${currentDay.getFullYear()}-${currentDay.getMonth()}-${currentDay.getDate()}`}
          className={`min-h-24 p-1 border border-gray-200 ${
            isToday ? 'bg-blue-50' :
            isYesterday ? 'bg-gray-100' :
            'bg-white'
          }`}
        >
          <div className={`text-xs font-semibold ${
            isToday ? 'text-blue-600' :
            isYesterday ? 'text-gray-500' :
            'text-gray-600'
          } mb-1`}>
            {isNewMonth ? `${dayNumber} ${monthName}` : dayNumber}
          </div>
          <div className="space-y-1">
            {dayEvents.slice(0, 3).map(event => (
              <div
                key={event.id}
                className="text-xs p-1 rounded cursor-pointer hover:opacity-80 transition-opacity"
                style={{ backgroundColor: event.color + '20', borderLeft: `3px solid ${event.color}` }}
                onClick={() => setSelectedEvent(event)}
                title={event.title}
              >
                <div className="truncate font-medium">{event.title}</div>
              </div>
            ))}
            {dayEvents.length > 3 && (
              <div className="text-xs text-gray-500 pl-1">
                +{dayEvents.length - 3} más
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="grid grid-cols-7 gap-0">
          {weekDays.map(day => (
            <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0 border border-gray-200">
          {days}
        </div>
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Cargando calendario...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Calendar className="h-8 w-8 mr-3 text-blue-600" />
                {workshop?.name}
              </h1>
              <p className="text-gray-600 mt-1">Calendario de Producciones</p>
            </div>
            <div className="text-right text-sm text-gray-500">
              {workshop?.email && <div>{workshop.email}</div>}
              {workshop?.phone && <div>{workshop.phone}</div>}
            </div>
          </div>

          {renderCalendar()}
        </div>
      </div>

      {selectedEvent && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedEvent(null)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{selectedEvent.title}</h3>
              <div
                className="w-6 h-6 rounded"
                style={{ backgroundColor: selectedEvent.color }}
              ></div>
            </div>

            {selectedEvent.description && (
              <div className="mb-4">
                <p className="text-sm text-gray-600">{selectedEvent.description}</p>
              </div>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Fecha inicio:</span>
                <span className="font-medium">{formatDate(selectedEvent.start_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Fecha fin:</span>
                <span className="font-medium">{formatDate(selectedEvent.end_date)}</span>
              </div>
            </div>

            <button
              onClick={() => setSelectedEvent(null)}
              className="mt-6 w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicWorkshopCalendar;
